<script setup lang="ts">
import type { TocItemData } from '~/utils/literature/hierarchy-builder'

const props = defineProps<{
  sections: Array<{ id: string, title?: string }>
  disabled?: boolean
}>()

const emit = defineEmits<{
  select: [id: string]
}>()

const { scrollToElement } = useScrollToElement('content')
const containers = inject(diKeys.containers, null)

const selectedSectionId = defineModel<string | undefined>()

const tocSections = computed(() => {
  const hasMultipleSections = props.sections.length > 1

  return props.sections
    .map((section, i) => ({
      id: section.id,
      title: section.title || (hasMultipleSections ? `Del ${i + 1}` : ''),
      fallbackIndex: hasMultipleSections ? i + 1 : undefined,
    }))
    .filter(section => section.title)
})

function handleSelect(item: TocItemData) {
  selectedSectionId.value = item.id
  emit('select', item.id)

  const targetElement = document.getElementById(item.id)
  const container = toValue(containers?.content)

  if (targetElement && container) {
    scrollInstantWithCorrection(targetElement, container)
    return
  }

  scrollToElement(item.id)
}

useTocExpandProvider()
</script>

<template>
  <ul v-if="tocSections.length" class="flex flex-col gap-3xs">
    <TableOfContentItem
      v-for="section in tocSections"
      :key="section.id"
      :toc-item="{ id: section.id, title: section.title }"
      :fallback-index="section.fallbackIndex"
      :selected-id="selectedSectionId"
      :disabled="props.disabled"
      @select="handleSelect"
    />
  </ul>
</template>
